// Máy POS: hàng chờ yêu cầu thu tiền thẻ do CMS gửi sang — báo khi có yêu cầu mới, thu ngân thu tiền hoặc từ chối
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Empty, Input, Modal, notification } from 'antd';
import dayjs from 'dayjs';
import { HandCoins } from 'lucide-react';
import { branchName } from '../POS/data';
import usePosStore from '../POS/store';
import { formatPhone } from '../POS/utils';
import type { PayRequest } from './data';
import useTreatmentStore from './store';
import { useTreatmentAuth } from './actions';
import { PayModal } from './modals';
import { money } from './ui';

export default function PosPayRequests() {
  const requests = useTreatmentStore(s => s.payRequests);
  const cards = useTreatmentStore(s => s.cards);
  const rejectPayRequest = useTreatmentStore(s => s.rejectPayRequest);
  const branchId = usePosStore(s => s.workingBranch);
  const { userName } = useTreatmentAuth();
  const [open, setOpen] = useState(false);
  const [paying, setPaying] = useState<PayRequest | null>(null);
  const [rejecting, setRejecting] = useState<{ r: PayRequest; reason: string } | null>(null);

  const pending = useMemo(() => requests.filter(r => r.status === 'pending' && r.branchId === branchId).sort((a, b) => a.createdAt.localeCompare(b.createdAt)), [requests, branchId]);

  // Báo khi có yêu cầu mới gửi tới POS chi nhánh này
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = new Set(pending.map(r => r.id));
    if (seen.current) {
      pending.filter(r => !seen.current!.has(r.id)).forEach(r => notification.info({
        key: r.id, placement: 'topRight', duration: 10,
        message: 'Yêu cầu thu tiền thẻ mới',
        description: `${r.customerName} · ${money(r.amount)}đ · ${r.installmentLabel} (từ ${r.createdBy})`,
        onClick: () => { notification.destroy(r.id); setOpen(true); },
      }));
    }
    seen.current = ids;
  }, [pending]);

  const card = paying ? cards.find(c => c.id === paying.cardId) || null : null;

  return (
    <>
      <Badge count={pending.length} size="small" offset={[-4, 4]}>
        <button onClick={() => setOpen(true)} title="Yêu cầu thu tiền thẻ liệu trình từ CMS"
          className={`flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-sm font-semibold ${pending.length ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          <HandCoins className="h-4 w-4" /><span className="hidden md:inline">Thu thẻ</span>
        </button>
      </Badge>

      <Modal open={open} onCancel={() => setOpen(false)} footer={null} centered width={600} title={`Yêu cầu thu tiền thẻ – POS ${branchName(branchId)}`}>
        {pending.length === 0 ? <Empty description="Không có yêu cầu nào đang chờ" /> : (
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {pending.map(r => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 text-sm">
                <div className="min-w-0">
                  <div><b>{r.customerName}</b> <span className="text-slate-500">{formatPhone(r.customerPhone)}</span></div>
                  <div className="text-xs text-slate-500">{r.packageName} · {r.installmentLabel} · <span className="font-mono">{r.id}</span></div>
                  <div className="text-xs text-slate-400">Gửi bởi {r.createdBy} lúc {dayjs(r.createdAt).format('HH:mm')}{r.note && ` · “${r.note}”`}</div>
                </div>
                <div className="flex items-center gap-2">
                  <b className="text-base text-[#2c6b7f]">{money(r.amount)}đ</b>
                  <button onClick={() => setRejecting({ r, reason: '' })} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">Từ chối</button>
                  <button onClick={() => { setOpen(false); setPaying(r); }} className="rounded-lg bg-[#357f96] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2c6b7f]">Thu tiền</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!rejecting} title="Từ chối yêu cầu thu tiền" centered okText="Từ chối" okButtonProps={{ danger: true, disabled: (rejecting?.reason.trim().length || 0) < 3 }} cancelText="Đóng"
        onCancel={() => setRejecting(null)}
        onOk={() => { if (rejecting) { rejectPayRequest(rejecting.r.id, userName, rejecting.reason.trim()); setRejecting(null); } }}>
        <p className="mb-2 text-sm text-slate-600">CMS ({rejecting?.r.createdBy}) sẽ nhận thông báo kèm lý do.</p>
        <Input.TextArea autoFocus rows={2} maxLength={200} value={rejecting?.reason} placeholder="VD: khách chưa ra quầy / khách hẹn chuyển khoản sau" onChange={e => rejecting && setRejecting({ ...rejecting, reason: e.target.value })} />
      </Modal>

      <PayModal card={card} request={paying} onClose={() => setPaying(null)} />
    </>
  );
}
